import { useRef, useState } from 'react';
import type { ConflictResolution, UploadProgress } from '../hooks/useUpload';

const s = {
  zone: { border: '2px dashed #b3b3b3', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' as const, cursor: 'pointer', transition: 'background 0.15s' } as const,
  zoneHover: { background: '#ececec' } as const,
  hint: { fontSize: '0.875rem', color: '#6b7280', marginTop: '0.4rem' } as const,
  progressWrap: { marginTop: '0.75rem' } as const,
  item: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', fontSize: '0.8rem' } as const,
  bar: { flex: 1, height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' } as const,
  clearBtn: { marginTop: '0.5rem', fontSize: '0.75rem', padding: '0.25rem 0.6rem', border: '1px solid #e5e7eb', borderRadius: '3px', background: '#fff', cursor: 'pointer', color: '#374151' } as const,
  conflictBox: { flexDirection: 'column' as const, alignItems: 'stretch' as const, gap: '0.35rem', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '0.5rem 0.6rem' } as const,
  conflictMsg: { color: '#b45309' } as const,
  conflictActions: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' as const } as const,
  conflictBtn: { fontSize: '0.75rem', padding: '0.25rem 0.6rem', border: '1px solid #e5e7eb', borderRadius: '3px', background: '#fff', cursor: 'pointer', color: '#374151' } as const,
  conflictCancelBtn: { fontSize: '0.75rem', padding: '0.25rem 0.6rem', border: '1px solid #b3b3b3', borderRadius: '3px', background: '#fff', cursor: 'pointer', color: '#1a1a1a' } as const,
  renameRow: { display: 'flex', gap: '0.4rem' } as const,
  renameInput: { flex: 1, padding: '0.25rem 0.4rem', fontSize: '0.75rem', border: '1px solid #999999', borderRadius: '3px' } as const,
};

function suggestAltName(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return `${name} (1)`;
  return `${name.slice(0, dot)} (1)${name.slice(dot)}`;
}

function fillStyle(pct: number, status: UploadProgress['status']): React.CSSProperties {
  return {
    height: '100%',
    width: `${pct}%`,
    background: status === 'error' ? '#999999' : status === 'done' ? '#666666' : '#1a1a1a',
    transition: 'width 0.2s',
  };
}

interface UploadZoneProps {
  uploads: UploadProgress[];
  onUpload: (files: File[]) => void;
  onClear: () => void;
  onResolveConflict: (fileId: string, resolution: ConflictResolution) => void;
}

export function UploadZone({ uploads, onUpload, onClear, onResolveConflict }: UploadZoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const startRename = (u: UploadProgress) => {
    setRenamingId(u.fileId);
    setRenameValue(suggestAltName(u.name));
  };

  const confirmRename = (fileId: string) => {
    if (!renameValue.trim()) return;
    onResolveConflict(fileId, { type: 'rename', newName: renameValue.trim() });
    setRenamingId(null);
  };

  const handleFiles = (files: FileList | null) => {
    if (files && files.length > 0) onUpload(Array.from(files));
  };

  const hasDone = uploads.some((u) => u.status === 'done' || u.status === 'error');

  return (
    <div>
      <div
        style={{ ...s.zone, ...(dragging ? s.zoneHover : {}) }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
      >
        <div>クリックまたはファイルをドラッグしてアップロード</div>
        <div style={s.hint}>1ファイルあたり最大 5 GB</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
          onClick={(e) => { (e.target as HTMLInputElement).value = ''; }}
        />
      </div>

      {uploads.length > 0 && (
        <div style={s.progressWrap}>
          {uploads.map((u) =>
            u.status === 'conflict' ? (
              <div key={u.fileId} style={{ ...s.item, ...s.conflictBox }}>
                <span style={s.conflictMsg}>「{u.name}」と同名のファイルが既に存在します</span>
                {renamingId === u.fileId ? (
                  <div style={s.renameRow}>
                    <input
                      style={s.renameInput}
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(u.fileId); if (e.key === 'Escape') setRenamingId(null); }}
                      autoFocus
                    />
                    <button style={s.conflictBtn} onClick={() => confirmRename(u.fileId)}>保存</button>
                    <button style={s.conflictBtn} onClick={() => setRenamingId(null)}>キャンセル</button>
                  </div>
                ) : (
                  <div style={s.conflictActions}>
                    <button style={s.conflictBtn} onClick={() => onResolveConflict(u.fileId, { type: 'overwrite' })}>上書き</button>
                    <button style={s.conflictBtn} onClick={() => startRename(u)}>別名で保存</button>
                    <button style={s.conflictCancelBtn} onClick={() => onResolveConflict(u.fileId, { type: 'cancel' })}>キャンセル</button>
                  </div>
                )}
              </div>
            ) : (
              <div key={u.fileId} style={s.item}>
                <span style={{ minWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                <div style={s.bar}><div style={fillStyle(u.progress, u.status)} /></div>
                <span style={{ minWidth: '90px', color: u.status === 'error' ? '#999999' : u.status === 'done' ? '#666666' : '#374151' }}>
                  {u.status === 'done' ? '✓ 完了' : u.status === 'error' ? `✗ ${u.error ?? 'エラー'}` : `${u.progress}%`}
                </span>
              </div>
            )
          )}
          {hasDone && <button style={s.clearBtn} onClick={onClear}>完了済みをクリア</button>}
        </div>
      )}
    </div>
  );
}
